import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Calculator, ExternalLink, Info, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, ReferenceLine, Cell,
} from 'recharts';

const COLORS = {
  ink: '#1a1a2e',
  copper: '#A14A3A',
  blue: '#2F6F8F',
  gold: '#8A6E3E',
  green: '#5D7C54',
  purple: '#6C4C8F',
  grey: '#6F7B87',
  pale: '#e8e4da',
};

const CPI_MONTH = 'April 2026';
const RELEASE_DATE = 'May 19, 2026';

const MAJOR_COMPONENTS = [
  { key: 'transportation', label: 'Transportation', change: 7.6, weight: 16.90, index: 183.5, color: COLORS.copper, note: 'Gasoline was the largest driver, up 28.6% year over year.' },
  { key: 'food', label: 'Food', change: 3.5, weight: 16.91, index: 201.3, color: COLORS.gold, note: 'Food purchased from stores rose 3.8% year over year.' },
  { key: 'health', label: 'Health & personal care', change: 3.3, weight: 5.05, index: 157.9, color: COLORS.purple, note: 'Above headline inflation in the April CPI release.' },
  { key: 'clothing', label: 'Clothing & footwear', change: 2.0, weight: 4.38, index: 96.1, color: COLORS.blue, note: 'Rebounded after a year-over-year decline in March.' },
  { key: 'shelter', label: 'Shelter', change: 1.8, weight: 29.41, index: 190.2, color: COLORS.green, note: 'Rent slowed to 3.6%, but rent was still up 30.8% since April 2021.' },
  { key: 'alcohol', label: 'Alcohol, tobacco & cannabis', change: 1.1, weight: 3.99, index: 202.1, color: '#9A9A8C', note: 'Below headline inflation in April.' },
  { key: 'recreation', label: 'Recreation, education & reading', change: 1.0, weight: 10.12, index: 129.7, color: '#7B5A46', note: 'Held down by a large year-over-year decline in travel tours.' },
  { key: 'household', label: 'Household operations & furnishings', change: 0.4, weight: 13.25, index: 132.9, color: '#4E5965', note: 'The slowest major CPI component in April.' },
];

const SPECIAL_ITEMS = [
  { label: 'Fuel oil & other fuels', change: 41.3, type: 'up' },
  { label: 'Gasoline', change: 28.6, type: 'up' },
  { label: 'Energy', change: 19.2, type: 'up' },
  { label: 'Property taxes & special charges', change: 5.6, type: 'up' },
  { label: 'Food purchased from stores', change: 3.8, type: 'up' },
  { label: 'Rent', change: 3.6, type: 'up' },
  { label: 'Food purchased from restaurants', change: 3.0, type: 'up' },
  { label: 'Travel tours', change: -11.0, type: 'down' },
  { label: "Homeowners' replacement cost", change: -2.4, type: 'down' },
  { label: 'Other owned accommodation expenses', change: -2.7, type: 'down' },
];

const CPI_TREND = [
  { month: 'Apr 2025', cpi: 1.7, exGas: 2.6 },
  { month: 'May 2025', cpi: 1.7, exGas: 2.6 },
  { month: 'Jun 2025', cpi: 1.9, exGas: 2.6 },
  { month: 'Jul 2025', cpi: 1.7, exGas: 2.5 },
  { month: 'Aug 2025', cpi: 1.9, exGas: 2.4 },
  { month: 'Sep 2025', cpi: 2.4, exGas: 2.4 },
  { month: 'Oct 2025', cpi: 2.2, exGas: 2.7 },
  { month: 'Nov 2025', cpi: 2.2, exGas: 2.4 },
  { month: 'Dec 2025', cpi: 2.4, exGas: 2.4 },
  { month: 'Jan 2026', cpi: 2.3, exGas: 2.0 },
  { month: 'Feb 2026', cpi: 1.8, exGas: 1.8 },
  { month: 'Mar 2026', cpi: 2.4, exGas: 2.2 },
  { month: 'Apr 2026', cpi: 2.8, exGas: 2.0 },
];

const DEFAULT_BUDGET = {
  shelter: 2200,
  food: 850,
  transportation: 650,
  household: 350,
  recreation: 250,
  health: 180,
  clothing: 150,
  alcohol: 100,
};

function pct(n) {
  return `${Number(n).toFixed(1)}%`;
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
}

const tipBox = {
  background: COLORS.ink,
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  padding: '10px 14px',
  fontSize: 13,
  color: COLORS.pale,
  minWidth: 220,
};

const InflationTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>12-month change</span>
        <span className="tabular">{pct(row.change)}</span>
      </div>
      {row.weight !== undefined && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginTop: 3 }}>
          <span style={{ color: 'rgba(232,228,218,0.7)' }}>CPI basket weight</span>
          <span className="tabular">{pct(row.weight)}</span>
        </div>
      )}
    </div>
  );
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={tipBox}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginTop: 3 }}>
          <span style={{ color: 'rgba(232,228,218,0.7)' }}>{p.name}</span>
          <span className="tabular">{pct(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

function StatCard({ kicker, value, text }) {
  return (
    <div className="bg-ink-800 text-cream-100 rounded-sm p-6">
      <div className="kicker text-copper-500 mb-3">{kicker}</div>
      <div className="font-serif text-[40px] leading-none tabular">{value}</div>
      <p className="mt-3 text-[13px] text-cream-100/65 leading-relaxed">{text}</p>
    </div>
  );
}

function CategoryTable({ rows, selectedKey, setSelectedKey }) {
  return (
    <div className="bg-cream-50 border border-ink-300/60 rounded-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-ink-300/60 bg-white/35">
        <div className="kicker text-copper-600">Major CPI components</div>
        <div className="mt-1 text-[13.5px] text-ink-500">Click a line to inspect the context.</div>
      </div>
      {rows.map((row) => (
        <button
          key={row.key}
          onClick={() => setSelectedKey(row.key)}
          className={`w-full text-left grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-3 border-b border-ink-300/60 transition ${selectedKey === row.key ? 'bg-cream-100' : 'hover:bg-cream-100/70'}`}
        >
          <div className="flex items-start gap-3">
            <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: row.color }} />
            <div>
              <div className="text-[15px] font-medium text-ink-800">{row.label}</div>
              <div className="text-[12.5px] text-ink-500 mt-0.5">Basket weight: {pct(row.weight)}</div>
            </div>
          </div>
          <div className="hidden sm:block font-mono text-[13.5px] text-ink-500 tabular">index {row.index}</div>
          <div className="font-mono text-[15px] text-ink-800 tabular">{pct(row.change)}</div>
        </button>
      ))}
    </div>
  );
}

function BudgetCalculator() {
  const [budget, setBudget] = useState(DEFAULT_BUDGET);

  const result = useMemo(() => {
    const total = Object.values(budget).reduce((sum, value) => sum + Number(value || 0), 0);
    const weighted = MAJOR_COMPONENTS.reduce((sum, row) => {
      const value = Number(budget[row.key] || 0);
      return sum + value * row.change;
    }, 0);
    const rate = total ? weighted / total : 0;
    const annualCost = total * 12 * (rate / 100);
    return { total, rate, annualCost };
  }, [budget]);

  const update = (key, value) => {
    const numeric = Math.max(0, Number(value || 0));
    setBudget((current) => ({ ...current, [key]: numeric }));
  };

  return (
    <section id="calculator" className="scroll-mt-8 mt-8 bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
      <div className="flex items-start gap-3 mb-6">
        <Calculator size={19} className="text-copper-600 mt-1 shrink-0" />
        <div>
          <div className="kicker text-copper-600 mb-3">Personal inflation estimate</div>
          <h2 className="font-serif text-[32px] leading-tight text-ink-800">Your basket can rise faster or slower than the headline CPI</h2>
          <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600 max-w-3xl">
            Enter a rough monthly household budget. This simplified estimator applies April 2026 CPI component changes to your spending mix. It is not a substitute for Statistics Canada's official Personal Inflation Calculator, but it makes the weighting idea visible.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_0.72fr] gap-8">
        <div className="grid sm:grid-cols-2 gap-4">
          {MAJOR_COMPONENTS.map((row) => (
            <label key={row.key} className="block border border-ink-300/60 bg-white/40 rounded-sm p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-[13.5px] font-medium text-ink-700">{row.label}</span>
                <span className="text-[12px] text-ink-500 tabular">{pct(row.change)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-ink-500">$</span>
                <input
                  type="number"
                  min="0"
                  value={budget[row.key] ?? 0}
                  onChange={(e) => update(row.key, e.target.value)}
                  className="w-full bg-cream-100 border border-ink-300/70 rounded-sm px-3 py-2 text-[15px] text-ink-800 outline-none focus:border-copper-500"
                />
              </div>
            </label>
          ))}
        </div>

        <div className="bg-ink-800 text-cream-100 rounded-sm p-6 h-fit sticky top-6">
          <div className="kicker text-copper-500 mb-4">Estimated household result</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-serif text-[40px] leading-none tabular">{pct(result.rate)}</div>
              <div className="text-[12px] text-cream-100/55 mt-1">estimated annual rate</div>
            </div>
            <div>
              <div className="font-serif text-[40px] leading-none tabular">{money(result.total)}</div>
              <div className="text-[12px] text-cream-100/55 mt-1">monthly budget entered</div>
            </div>
          </div>
          <div className="mt-6 border-t border-cream-100/15 pt-5">
            <div className="text-[13px] text-cream-100/65">Estimated added annual cost at this mix:</div>
            <div className="font-serif text-[42px] leading-none tabular mt-2">{money(result.annualCost)}</div>
          </div>
          <p className="mt-5 text-[12.5px] text-cream-100/55 leading-relaxed">
            This uses major components only. Detailed subcategories such as gasoline and rent can diverge sharply from their parent groups.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function Inflation() {
  const [selectedKey, setSelectedKey] = useState('transportation');
  const selected = MAJOR_COMPONENTS.find((row) => row.key === selectedKey) || MAJOR_COMPONENTS[0];

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      <section className="bg-ink-800 text-cream-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-cream-100/65 hover:text-copper-500 mb-6 transition">
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="kicker text-copper-500 mb-4">Inflation</div>
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-end">
            <div>
              <h1 className="font-serif text-[52px] md:text-[76px] leading-[0.96] tracking-tight">
                What got more<br />expensive?
              </h1>
              <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
                The headline CPI rose 2.8% year over year in {CPI_MONTH}, but households do not buy the headline. This page breaks the inflation number into major spending categories, special pressure points and a simple household basket estimator.
              </p>
            </div>
            <div className="bg-white/5 border border-cream-100/15 rounded-sm p-6 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-copper-400 mb-4">
                <TrendingUp size={18} />
                <span className="kicker">Latest CPI snapshot</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-serif text-[52px] leading-none tabular">2.8%</div>
                  <div className="text-[12px] text-cream-100/55 mt-1">all-items CPI</div>
                </div>
                <div>
                  <div className="font-serif text-[52px] leading-none tabular">2.0%</div>
                  <div className="text-[12px] text-cream-100/55 mt-1">excluding gasoline</div>
                </div>
              </div>
              <div className="mt-5 text-[12.5px] leading-relaxed text-cream-100/55">
                Statistics Canada release: {RELEASE_DATE}. Next CPI release listed by StatCan: June 22, 2026.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-10">
            <a href="#components" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition">Major components</a>
            <a href="#pressure-points" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition">Pressure points</a>
            <a href="#calculator" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition">Personal estimate</a>
            <a href="#method" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition">Method & sources</a>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <StatCard kicker="Fastest major component" value="7.6%" text="Transportation led the major CPI groups in April, driven mainly by gasoline." />
          <StatCard kicker="Food from stores" value="3.8%" text="Grocery prices rose faster than the all-items CPI in the April release." />
          <StatCard kicker="Rent pressure" value="30.8%" text="Rent prices were 30.8% higher than April 2021, even though annual rent inflation slowed." />
        </section>

        <section id="components" className="scroll-mt-8 grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-stretch">
          <CategoryTable rows={MAJOR_COMPONENTS} selectedKey={selectedKey} setSelectedKey={setSelectedKey} />

          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-5 md:p-7">
            <div className="kicker text-copper-600 mb-4">12-month change by major component</div>
            <div className="h-[390px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={MAJOR_COMPONENTS} layout="vertical" margin={{ top: 8, right: 26, bottom: 8, left: 148 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(47,55,66,0.15)" horizontal={false} />
                  <XAxis type="number" domain={[0, 8.5]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12, fill: '#6F7B87' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: '#2F3742' }} width={158} axisLine={false} tickLine={false} />
                  <Tooltip content={<InflationTooltip />} cursor={{ fill: 'rgba(200,137,74,0.08)' }} />
                  <ReferenceLine x={2.8} stroke="#A14A3A" strokeDasharray="4 4" />
                  <Bar dataKey="change" radius={[0, 4, 4, 0]}>
                    {MAJOR_COMPONENTS.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-[12.5px] text-ink-500">Dashed line marks the all-items CPI rate of 2.8%.</div>
          </div>
        </section>

        <section className="mt-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-stretch">
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
            <div className="kicker text-copper-600 mb-4">Selected category</div>
            <div className="flex items-start justify-between gap-6">
              <div>
                <h2 className="font-serif text-[34px] leading-tight text-ink-800">{selected.label}</h2>
                <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600 max-w-xl">{selected.note}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-serif text-[44px] leading-none text-ink-800 tabular">{pct(selected.change)}</div>
                <div className="text-[12px] text-ink-500 mt-1">12-month change</div>
              </div>
            </div>
            <div className="mt-8 grid md:grid-cols-2 gap-4">
              <div className="border border-ink-300/60 bg-white/40 rounded-sm p-4">
                <div className="kicker text-ink-500 mb-2">CPI basket weight</div>
                <div className="font-serif text-[34px] leading-none text-ink-800 tabular">{pct(selected.weight)}</div>
                <div className="text-[12px] text-ink-500 mt-1">2024 basket weights at April 2025 prices</div>
              </div>
              <div className="border border-ink-300/60 bg-white/40 rounded-sm p-4">
                <div className="kicker text-ink-500 mb-2">Index level</div>
                <div className="font-serif text-[34px] leading-none text-ink-800 tabular">{selected.index}</div>
                <div className="text-[12px] text-ink-500 mt-1">2002 = 100</div>
              </div>
            </div>
          </div>

          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
            <div className="kicker text-copper-600 mb-4">Headline vs excluding gasoline</div>
            <div className="h-[310px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={CPI_TREND} margin={{ top: 8, right: 24, bottom: 4, left: 2 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(47,55,66,0.15)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6F7B87' }} axisLine={false} tickLine={false} interval={1} />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12, fill: '#6F7B87' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<TrendTooltip />} />
                  <Line type="monotone" dataKey="cpi" name="All-items CPI" stroke={COLORS.copper} strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="exGas" name="Excluding gasoline" stroke={COLORS.blue} strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-[12.5px] text-ink-500">
              <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.copper }} />All-items CPI</span>
              <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.blue }} />CPI excluding gasoline</span>
            </div>
          </div>
        </section>

        <section id="pressure-points" className="scroll-mt-8 mt-8 bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
          <div className="kicker text-copper-600 mb-4">What moved the number?</div>
          <h2 className="font-serif text-[32px] leading-tight text-ink-800">The headline was pushed up by energy, while some travel and housing-related lines pulled down</h2>
          <div className="mt-7 grid md:grid-cols-2 gap-6">
            <div>
              <div className="kicker text-ink-500 mb-3">Main upward contributors</div>
              <div className="space-y-2">
                {SPECIAL_ITEMS.filter((item) => item.type === 'up').map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-4 border-b border-ink-300/50 pb-2 text-[14px]">
                    <span className="text-ink-600">{item.label}</span>
                    <span className="font-mono tabular text-ink-800">{pct(item.change)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="kicker text-ink-500 mb-3">Main downward contributors</div>
              <div className="space-y-2">
                {SPECIAL_ITEMS.filter((item) => item.type === 'down').map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-4 border-b border-ink-300/50 pb-2 text-[14px]">
                    <span className="text-ink-600">{item.label}</span>
                    <span className="font-mono tabular text-ink-800">{pct(item.change)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <BudgetCalculator />

        <section id="method" className="scroll-mt-8 mt-8 bg-cream-50 border border-ink-300/60 rounded-sm p-7 md:p-8">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-copper-600 mt-1 shrink-0" />
            <div>
              <div className="kicker text-copper-600 mb-3">Method & sources</div>
              <h2 className="font-serif text-[32px] leading-tight text-ink-800">How this page reads the CPI</h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600 max-w-3xl">
                The page uses Statistics Canada's April 2026 Consumer Price Index release. Major-component rates and basket weights come from Table 1 of the release. The pressure-point list uses the release narrative and Table 5. The trend uses the Bank of Canada's CPI series for all-items CPI and CPI excluding gasoline.
              </p>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <a href="https://www150.statcan.gc.ca/n1/daily-quotidien/260519/dq260519a-eng.htm" target="_blank" rel="noreferrer" className="border border-ink-300/60 rounded-sm p-4 hover:bg-cream-100 transition">
                  <div className="flex items-center gap-2 font-medium text-ink-800">Statistics Canada CPI, April 2026 <ExternalLink size={14} /></div>
                  <div className="text-[12.5px] text-ink-500 mt-1">Headline CPI, category narrative, regional notes and release schedule.</div>
                </a>
                <a href="https://www150.statcan.gc.ca/n1/daily-quotidien/260519/t001a-eng.htm" target="_blank" rel="noreferrer" className="border border-ink-300/60 rounded-sm p-4 hover:bg-cream-100 transition">
                  <div className="flex items-center gap-2 font-medium text-ink-800">Table 1: major components <ExternalLink size={14} /></div>
                  <div className="text-[12.5px] text-ink-500 mt-1">Major CPI components, basket weights and 12-month percentage changes.</div>
                </a>
                <a href="https://www150.statcan.gc.ca/n1/daily-quotidien/260519/t005a-eng.htm" target="_blank" rel="noreferrer" className="border border-ink-300/60 rounded-sm p-4 hover:bg-cream-100 transition">
                  <div className="flex items-center gap-2 font-medium text-ink-800">Table 5: contributors <ExternalLink size={14} /></div>
                  <div className="text-[12.5px] text-ink-500 mt-1">Main upward and downward contributors to monthly and annual CPI changes.</div>
                </a>
                <a href="https://www.bankofcanada.ca/stats/cpi" target="_blank" rel="noreferrer" className="border border-ink-300/60 rounded-sm p-4 hover:bg-cream-100 transition">
                  <div className="flex items-center gap-2 font-medium text-ink-800">Bank of Canada CPI series <ExternalLink size={14} /></div>
                  <div className="text-[12.5px] text-ink-500 mt-1">Monthly all-items CPI and CPI excluding gasoline series.</div>
                </a>
              </div>
              <p className="mt-6 text-[12.5px] text-ink-500 leading-relaxed max-w-3xl">
                The calculator is intentionally simple. It applies published 12-month percentage changes for the eight major components to user-entered monthly spending. It does not model detailed subcomponents, substitutions, regional indexes or taxes.
              </p>
              <p className="mt-3 text-[12.5px] text-ink-500 leading-relaxed max-w-3xl">
                Note: the basket weights shown here use the 2024 reference period. Statistics Canada updates the CPI basket to 2025 weights with the May 2026 release on June 22, 2026, so component weights will shift slightly thereafter.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
